import { getLineDisplay } from "./DisplayService";
import { SPORTS_LIST, BetQLSportItem } from "../bet-config";

export interface BetQLMappedPowerRating {
  displayRating: number;
  favoriteText: string;
  favoriteData: string;
  leagueName: string;
  noData: boolean;
  rating?: number;
  teamId?: string | null;
  useUnderdog?: boolean;
}

export interface BetQLEventTrend {
  [key: string]: any;
  id: string;
  teamId: string;
  timePeriod: string;
  description: string;
  typeGames: number;
  rating: number;
  trendType: string;
  reportType: string;
}

export interface BetQLPowerRating {
  rating: number;
  differential: number;
  preferredLine: number;
  preferredTeamId: string;
  lineFavoriteTeamId: string;
  preferredTotal: string;
  preferredMoneyline: string;
}

export interface BetQLMappedTrend {
  leagueName: string;
  filterType: string;
  title: string;
  rating: number;
  displayRating: number;
  description: string;
  teamId: string;
  games: number;
  favoriteText: string;
  favoriteData: number;
}

export const roundRating = (rating: number) => {
  if (Math.abs(rating) >= 5) {
    return 5;
  }
  const decimal = parseFloat((Math.abs(rating) % 1).toFixed(2));
  const integer = Math.trunc(Math.abs(rating));
  if (decimal < 0.25) {
    return integer;
  }
  if (decimal >= 0.25 && decimal < 0.75) {
    return integer + 0.5;
  }
  // decimal >= 0.75
  return integer + 1;
};

export const getRatingDisplayValues = (
  currentSport: string,
  spRating?: BetQLPowerRating,
  mlRating?: BetQLPowerRating,
  ouRating?: BetQLPowerRating,
  sp1HRating?: BetQLPowerRating,
  ml1HRating?: BetQLPowerRating,
  ou1HRating?: BetQLPowerRating,
  sp1PRating?: BetQLPowerRating,
  ml1PRating?: BetQLPowerRating,
  ou1PRating?: BetQLPowerRating
) => {
  const currentSportObj: BetQLSportItem = SPORTS_LIST[currentSport];
  const retValue: any = {};
  if (currentSportObj.hasFGspreadPowerRating && spRating) {
    retValue.valueFGspreadRating = spRating.rating;
    retValue.valueFGspreadDifferential = spRating.differential;
    retValue.valueFGspreadPreferredLine = spRating.preferredLine;
    retValue.valueFGspreadPreferredTeamId = spRating.preferredTeamId;
  }
  if (currentSportObj.hasFGmoneylinePowerRating && mlRating) {
    retValue.valueFGmoneylineRating = mlRating.rating;
    retValue.valueFGmoneylineDifferential = mlRating.differential;
    retValue.valueFGmoneylinePreferredLine = mlRating.preferredLine;
    retValue.valueFGmoneylinePreferredTeamId = mlRating.preferredTeamId;
    retValue.valueFGmoneylinePreferredMoneyline = mlRating.preferredMoneyline;
  }
  if (currentSportObj.hasFGtotalPowerRating && ouRating) {
    retValue.valueFGtotalRating = ouRating.rating;
    retValue.valueFGtotalDifferential = ouRating.differential;
    retValue.valueFGtotalPreferredLine = ouRating.preferredLine;
    retValue.valueFGtotalPreferredTotal = ouRating.preferredTotal;
  }
  if (currentSportObj.has1HspreadPowerRating && sp1HRating) {
    retValue.value1HspreadRating = sp1HRating.rating;
    retValue.value1HspreadDifferential = sp1HRating.differential;
    retValue.value1HspreadPreferredLine = sp1HRating.preferredLine;
    retValue.value1HspreadPreferredTeamId = sp1HRating.preferredTeamId;
  }
  if (currentSportObj.has1HmoneylinePowerRating && ml1HRating) {
    retValue.value1HmoneylineRating = ml1HRating.rating;
    retValue.value1HmoneylineDifferential = ml1HRating.differential;
    retValue.value1HmoneylinePreferredLine = ml1HRating.preferredLine;
    retValue.value1HmoneylinePreferredTeamId = ml1HRating.preferredTeamId;
  }
  if (currentSportObj.has1HtotalPowerRating && ou1HRating) {
    retValue.value1HtotalRating = ou1HRating.rating;
    retValue.value1HtotalDifferential = ou1HRating.differential;
    retValue.value1HtotalPreferredLine = ou1HRating.preferredLine;
    retValue.value1HtotalPreferredTotal = ou1HRating.preferredTotal;
  }
  if (currentSportObj.has1PspreadPowerRating && sp1PRating) {
    retValue.value1PspreadRating = sp1PRating.rating;
    retValue.value1PspreadDifferential = sp1PRating.differential;
    retValue.value1PspreadPreferredLine = sp1PRating.preferredLine;
    retValue.value1PspreadPreferredTeamId = sp1PRating.preferredTeamId;
  }
  if (currentSportObj.has1PmoneylinePowerRating && ml1PRating) {
    retValue.value1PmoneylineRating = ml1PRating.rating;
    retValue.value1PmoneylineDifferential = ml1PRating.differential;
    retValue.value1PmoneylinePreferredLine = ml1PRating.preferredLine;
    retValue.value1PmoneylinePreferredTeamId = ml1PRating.preferredTeamId;
  }
  if (currentSportObj.has1PtotalPowerRating && ou1PRating) {
    retValue.value1PtotalRating = ou1PRating.rating;
    retValue.value1PtotalDifferential = ou1PRating.differential;
    retValue.value1PtotalPreferredLine = ou1PRating.preferredLine;
    retValue.value1PtotalPreferredTotal = ou1PRating.preferredTotal;
  }
  return retValue;
};

// Get the display value for the books favored line.
const getFavorite = (game: any, filterKey: string, teamKey: string, dataKey: string) => {
  let retValue: any = {
    favoriteText: "--",
    favoriteData: "--",
  };
  if (game[`current${filterKey}${dataKey}Display`] === 0) {
    retValue.favoriteText = "Even";
    retValue.favoriteData = "";
  } else {
    retValue.favoriteText = game[teamKey].preferredAbbreviation;
    retValue.favoriteData = game[`current${filterKey}${dataKey}Display`];
    // We don't need to alter the display value if the bet is 'total' type
    if (!filterKey.includes("total")) {
      retValue.favoriteData = retValue.favoriteData > 0 ? `+${retValue.favoriteData}` : retValue.favoriteData;
    }
  }
  return retValue;
};

const getMappedTrends = (game: any, title: string, filterKey: string, key: string, trends: BetQLEventTrend[]) => {
  let mappedTrends: BetQLMappedTrend[] = [];
  if (trends && trends.length) {
    let tempRoundedRating: number,
      tempRating: number,
      newTrend: BetQLMappedTrend,
      tempTeamId: string,
      favoriteData: {
        favoriteText: string;
        favoriteData: number;
      };
    mappedTrends = trends
      .slice()
      .map((trend: BetQLEventTrend) => {
        tempRating = trend[key];
        tempRoundedRating = roundRating(tempRating);
        if (tempRating > 0) {
          if (title === "Total") {
            favoriteData = {
              favoriteText: "Over",
              favoriteData: game[`current${filterKey}Display`],
            };
          } else if (trend.teamId === game.homeTeam.id) {
            tempTeamId = game.homeTeam.id;
            favoriteData = getFavorite(game, filterKey, "homeTeam", "Home");
          } else {
            tempTeamId = game.awayTeam.id;
            favoriteData = getFavorite(game, filterKey, "awayTeam", "Away");
          }
        } else {
          if (title === "Total") {
            favoriteData = {
              favoriteText: "Under",
              favoriteData: game[`current${filterKey}Display`],
            };
          } else if (trend.teamId === game.homeTeam.id) {
            tempTeamId = game.awayTeam.id;
            favoriteData = getFavorite(game, filterKey, "awayTeam", "Away");
          } else {
            tempTeamId = game.homeTeam.id;
            favoriteData = getFavorite(game, filterKey, "homeTeam", "Home");
          }
        }
        newTrend = {
          leagueName: game.league.name,
          filterType: title.toLowerCase(),
          title: `${tempRoundedRating} Star Trend - ${title}`,
          rating: trend[key],
          displayRating: tempRoundedRating,
          description: trend.description,
          teamId: tempTeamId,
          games: trend.games,
          ...favoriteData,
        };
        return newTrend;
      })
      .sort((trendA: BetQLMappedTrend, trendB: BetQLMappedTrend) => {
        if (trendA.displayRating > trendB.displayRating) {
          return -1;
        }
        if (trendA.displayRating < trendB.displayRating) {
          return 1;
        }
        return 0;
      });
  }
  return mappedTrends;
};

const getMLPowerRatingValues = (game: any, periodFilter: string, trend: BetQLMappedPowerRating) => {
  const powerRating = trend;
  const isHomeTeamPreferred = game[`value${periodFilter}PreferredTeamId`] === game.homeTeam?.id;
  const isHomeTeamFavored = game[`current${periodFilter}HomeData`] < game[`current${periodFilter}AwayData`];
  const isEven = game[`current${periodFilter}HomeData`] === game[`current${periodFilter}AwayData`];
  const useUnderdog = isHomeTeamPreferred !== isHomeTeamFavored;
  powerRating.useUnderdog = isEven ? false : useUnderdog;
  powerRating.rating = Math.abs(game[`value${periodFilter}Rating`]);
  if (game[`value${periodFilter}PreferredTeamId`] === game.homeTeam?.id) {
    powerRating.teamId = game.homeTeam?.id;
    powerRating.favoriteText = game.homeTeam?.preferredAbbreviation;
    powerRating.favoriteData = getLineDisplay(game[`current${periodFilter}HomeData`]);
  } else if (game[`value${periodFilter}PreferredTeamId`] === game.awayTeam?.id) {
    powerRating.teamId = game.awayTeam?.id;
    powerRating.favoriteText = game.awayTeam?.preferredAbbreviation;
    powerRating.favoriteData = getLineDisplay(game[`current${periodFilter}AwayData`]);
  } else if (game[`value${periodFilter}PreferredMoneyline`] === "draw") {
    powerRating.teamId = null;
    powerRating.favoriteText = "Draw";
    powerRating.favoriteData = getLineDisplay(game[`current${periodFilter}DrawData`]);
  }
  return powerRating;
};

const getOUPowerRatingValues = (game: any, periodFilter: string, trend: BetQLMappedPowerRating) => {
  let powerRating = { ...trend };
  powerRating.rating = Math.abs(game[`value${periodFilter}Rating`]);
  const totalText = game[`value${periodFilter}PreferredTotal`];
  if (totalText && totalText !== "--") {
    powerRating.favoriteText = totalText.charAt(0).toUpperCase() + totalText.slice(1);
    powerRating.favoriteData = game[`current${periodFilter}AwayDisplay`];
  }
  powerRating.favoriteData = game[`current${periodFilter}AwayDisplay`];
  return powerRating;
};

const getSPPowerRatingValues = (game: any, periodFilter: string, trend: BetQLMappedPowerRating) => {
  let powerRating = { ...trend };
  const isHomeTeamPreferred = game[`value${periodFilter}PreferredTeamId`] === game.homeTeam.id;
  const isHomeTeamFavored = game[`current${periodFilter}HomeData`] < game[`current${periodFilter}AwayData`];
  const isEven = game[`current${periodFilter}HomeData`] === game[`current${periodFilter}AwayData`];
  const differentSelection = isHomeTeamPreferred !== isHomeTeamFavored;
  const positiveDifference = game[`value${periodFilter}Differential`] > 0;
  const useUnderdog = differentSelection || (positiveDifference && !differentSelection);
  powerRating.useUnderdog = isEven ? false : useUnderdog;
  powerRating.rating = Math.abs(game[`value${periodFilter}Rating`]);
  if ((useUnderdog && isHomeTeamFavored) || (!useUnderdog && !isHomeTeamFavored)) {
    powerRating.teamId = game.awayTeam.id;
    powerRating.favoriteText = game.awayTeam.preferredAbbreviation;
    powerRating.favoriteData = getLineDisplay(game[`current${periodFilter}AwayData`]);
  } else {
    powerRating.teamId = game.homeTeam.id;
    powerRating.favoriteText = game.homeTeam.preferredAbbreviation;
    powerRating.favoriteData = getLineDisplay(game[`current${periodFilter}HomeData`]);
  }
  return powerRating;
};

export const getValueDisplayValues = (game: any, currentSport: string) => {
  const currentSportObj = SPORTS_LIST[currentSport];
  let spreadTrends: BetQLEventTrend[] = [],
    moneylineTrends: BetQLEventTrend[] = [],
    totalTrends: BetQLEventTrend[] = [],
    H1SpreadTrends: BetQLEventTrend[] = [],
    maxNewFGspreadTrend: BetQLMappedPowerRating = {
      displayRating: game.valueFGspreadRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        !game.valueFGspreadPreferredTeamId ||
        game.valueFGspreadPreferredLine === null ||
        game.valueFGspreadDifferential === null,
    },
    maxNewFGmoneylineTrend: BetQLMappedPowerRating = {
      displayRating: game.valueFGmoneylineRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData: game.valueFGmoneylinePreferredLine === null || game.valueFGmoneylinedifferential === null,
    },
    maxNewFGtotalTrend: BetQLMappedPowerRating = {
      displayRating: game.valueFGtotalRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        game.valueFGtotalPreferredTotal === null ||
        game.valueFGtotalPreferredLine === null ||
        game.valueFGtotalDifferential === null,
    },
    maxNew1HspreadTrend: BetQLMappedPowerRating = {
      displayRating: game.value1HspreadRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        !game.value1HspreadPreferredTeamId ||
        game.value1HspreadPreferredLine === null ||
        game.value1HspreadDifferential === null,
    },
    maxNew1HmoneylineTrend: BetQLMappedPowerRating = {
      displayRating: game.value1HmoneylineRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData: game.value1HmoneylinePreferredLine === null || game.value1Hmoneylinedifferential === null,
    },
    maxNew1HtotalTrend: BetQLMappedPowerRating = {
      displayRating: game.value1HtotalRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        game.value1HtotalPreferredTotal === null ||
        game.value1HtotalPreferredLine === null ||
        game.value1HtotalDifferential === null,
    },
    maxNew1PspreadTrend: BetQLMappedPowerRating = {
      displayRating: game.value1PspreadRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        !game.value1PspreadPreferredTeamId ||
        game.value1PspreadPreferredLine === null ||
        game.value1PspreadDifferential === null,
    },
    maxNew1PmoneylineTrend: BetQLMappedPowerRating = {
      displayRating: game.value1PmoneylineRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        !game.value1PmoneylinePreferredTeamId ||
        game.value1PmoneylinePreferredLine === null ||
        game.value1Pmoneylinedifferential === null,
    },
    maxNew1PtotalTrend: BetQLMappedPowerRating = {
      displayRating: game.value1PtotalRating,
      favoriteText: "--",
      favoriteData: "--",
      leagueName: game.league.name,
      noData:
        game.value1PtotalPreferredTotal === null ||
        game.value1PtotalPreferredLine === null ||
        game.value1PtotalDifferential === null,
    };

  if (game.eventTrends) {
    game.eventTrends.forEach((trend: BetQLEventTrend) => {
      switch (trend.trendType) {
        case "ats":
          spreadTrends.push(trend);
          break;
        case "ml":
          moneylineTrends.push(trend);
          break;
        case "ou":
          //totalTrends.push(trend);
          break;
        case "htats":
          H1SpreadTrends.push(trend);
          break;
      }
    });
  }
  const spreadMappedTrends: BetQLMappedTrend[] = getMappedTrends(game, "Spread", "FGspread", "rating", spreadTrends);
  const moneylineMappedTrends: BetQLMappedTrend[] = getMappedTrends(
    game,
    "Moneyline",
    "FGmoneyline",
    "rating",
    moneylineTrends
  );
  const totalMappedTrends: BetQLMappedTrend[] = getMappedTrends(game, "Total", "FGtotal", "rating", totalTrends);
  const H1SpreadMappedTrends: BetQLMappedTrend[] = getMappedTrends(
    game,
    "Half Time Spread",
    "1Hspread",
    "rating",
    H1SpreadTrends
  );
  if (currentSportObj.hasFGspreadPowerRating && !maxNewFGspreadTrend.noData) {
    maxNewFGspreadTrend = getSPPowerRatingValues(game, "FGspread", maxNewFGspreadTrend);
  }
  if (currentSportObj.hasFGmoneylinePowerRating && !maxNewFGmoneylineTrend.noData) {
    maxNewFGmoneylineTrend = getMLPowerRatingValues(game, "FGmoneyline", maxNewFGmoneylineTrend);
  }
  if (currentSportObj.hasFGtotalPowerRating && !maxNewFGtotalTrend.noData) {
    maxNewFGtotalTrend = getOUPowerRatingValues(game, "FGtotal", maxNewFGtotalTrend);
  }
  if (currentSportObj.has1HspreadPowerRating && !maxNew1HspreadTrend.noData) {
    maxNew1HspreadTrend = getSPPowerRatingValues(game, "1Hspread", maxNew1HspreadTrend);
  }
  if (currentSportObj.has1HmoneylinePowerRating && !maxNew1HmoneylineTrend.noData) {
    maxNew1HmoneylineTrend = getMLPowerRatingValues(game, "1Hmoneyline", maxNew1HmoneylineTrend);
  }
  if (currentSportObj.has1HtotalPowerRating && !maxNew1HtotalTrend.noData) {
    maxNew1HtotalTrend = getOUPowerRatingValues(game, "1Htotal", maxNew1HtotalTrend);
  }
  if (currentSportObj.has1PspreadPowerRating && !maxNew1PspreadTrend.noData) {
    maxNew1PspreadTrend = getSPPowerRatingValues(game, "1Pspread", maxNew1PspreadTrend);
  }
  if (currentSportObj.has1PmoneylinePowerRating && !maxNew1PmoneylineTrend.noData) {
    maxNew1PmoneylineTrend = getMLPowerRatingValues(game, "1Pmoneyline", maxNew1PmoneylineTrend);
  }
  if (currentSportObj.has1PtotalPowerRating && !maxNew1PtotalTrend.noData) {
    maxNew1PtotalTrend = getOUPowerRatingValues(game, "1Ptotal", maxNew1PtotalTrend);
  }

  let maxFGspreadTrend = spreadMappedTrends[0] ? spreadMappedTrends[0] : null,
    maxFGmoneylineTrend = moneylineMappedTrends[0] ? moneylineMappedTrends[0] : null,
    maxFGtotalTrend = totalMappedTrends[0] ? totalMappedTrends[0] : null,
    max1HspreadTrend = H1SpreadMappedTrends[0] ? H1SpreadMappedTrends[0] : null;
  let mappedTrends: BetQLMappedTrend[] = [];
  if (spreadMappedTrends.length) {
    mappedTrends = mappedTrends.concat(spreadMappedTrends);
  }
  if (moneylineMappedTrends.length) {
    mappedTrends = mappedTrends.concat(moneylineMappedTrends);
  }
  if (totalMappedTrends.length) {
    mappedTrends = mappedTrends.concat(totalMappedTrends);
  }
  if (H1SpreadMappedTrends.length) {
    mappedTrends = mappedTrends.concat(H1SpreadMappedTrends);
  }
  mappedTrends = mappedTrends
    .sort((trendA: BetQLMappedTrend, trendB: BetQLMappedTrend) => {
      if (trendA.displayRating > trendB.displayRating) {
        return -1;
      }
      if (trendA.displayRating < trendB.displayRating) {
        return 1;
      }
      return 0;
    })
    .reduce((array: BetQLMappedTrend[], trend: BetQLMappedTrend) => {
      if (array.length < 3) {
        array.push(trend);
      }
      return array;
    }, []);
  let valueFavoriteText = "--",
    valueFavoriteData = "--";
  const awayData = game[`currentFG${currentSportObj.favoriteLine}AwayData`];
  const homeData = game[`currentFG${currentSportObj.favoriteLine}HomeData`];
  if (awayData !== null && homeData !== null) {
    const home = homeData < awayData;
    valueFavoriteText = home ? game.homeTeam.preferredAbbreviation : game.awayTeam.preferredAbbreviation;
    valueFavoriteData = home
      ? game[`currentFG${currentSportObj.favoriteLine}HomeDisplay`]
      : game[`currentFG${currentSportObj.favoriteLine}AwayDisplay`];
  }
  return {
    mappedTrends,
    maxFGspreadTrend,
    maxFGmoneylineTrend,
    maxFGtotalTrend,
    max1HspreadTrend,
    maxNewFGspreadTrend,
    maxNewFGmoneylineTrend,
    maxNewFGtotalTrend,
    maxNew1HspreadTrend,
    maxNew1HmoneylineTrend,
    maxNew1HtotalTrend,
    maxNew1PspreadTrend,
    maxNew1PmoneylineTrend,
    maxNew1PtotalTrend,
    valueFavoriteText,
    valueFavoriteData,
  };
};
